/**
 * Failure reporting — because nothing currently reports failures at all.
 *
 * V5_HANDOFF §2 item 4: *"If a visitor loses their WebGL context or a GLB 404s,
 * nobody finds out."* That is exactly right, and this is the cheap thing that
 * beats the current zero.
 *
 * WHAT §2 PROPOSED, AND WHY IT WOULD NOT HAVE COVERED ITS OWN EXAMPLE
 *
 * §2 suggests `window.onerror`. That is here, and it is worth having — but it
 * does NOT catch WebGL context loss, which is the example §2 gives.
 * `webglcontextlost` is a DOM event dispatched on the canvas, not an exception,
 * so no global error handler ever sees it. That case is handled separately in
 * `game.ts`, on the canvas, where it has to be. Two different mechanisms for
 * two different kinds of failure, and neither substitutes for the other.
 *
 * The three that DO belong here:
 *
 *   - `error` — uncaught exceptions, including from callbacks and rAF.
 *   - `unhandledrejection` — a rejected promise nobody caught. This codebase is
 *     full of async model loads and fire-and-forget playback, and an unhandled
 *     rejection inside a mounted realm surfaces in the SITE's console rather
 *     than the game's, which is exactly the kind of thing that goes unnoticed.
 *   - `report()` — called directly for failures that are not exceptions at all.
 *     A GLB that 404s takes `GLTFLoader`'s error callback, not a throw.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 *
 * **It does not send anything anywhere.** No endpoint, no beacon, no third
 * party. That is a decision about the owner's visitors' data and about a
 * service that does not exist yet, and it is the owner's to make, not
 * implementation's. `subscribe()` is the seam it would attach to.
 *
 * What it does instead is make failures VISIBLE where someone will actually
 * look: the perf readout, which `DICTIONARY.md` already records as shipping in
 * production on purpose, "because the machines that matter are the visitor's".
 * A visitor who hits BACKQUOTE can read the failure count and the last message
 * back to the owner. That is a low bar and it is infinitely more than zero.
 */

/** One failure, as recorded. */
export interface ReportEntry {
  /** Milliseconds since page load, so entries can be ordered against each other. */
  readonly at: number;
  /** Where it came from — 'error', 'unhandledrejection', or a caller's label. */
  readonly source: string;
  readonly message: string;
}

/**
 * A bounded ring. A failing frame can throw sixty times a second, and an
 * unbounded log of that is itself a memory leak — which would be a poor thing
 * for the error reporter to introduce into a codebase whose one shipped crash
 * was memory.
 */
const MAX = 25;
const entries: ReportEntry[] = [];
const listeners = new Set<(e: ReportEntry) => void>();
let installed = false;

/** Longest single message kept. A stack trace can run to kilobytes. */
const MAX_MESSAGE = 300;

function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value) ?? String(value); } catch { return String(value); }
}

/**
 * Record a failure.
 *
 * Safe to call from anywhere, including from inside an error handler: it never
 * throws, and it never calls back into anything that could throw again.
 */
export function report(source: string, value: unknown): void {
  const entry: ReportEntry = {
    at: Math.round(performance.now()),
    source,
    message: describe(value).slice(0, MAX_MESSAGE),
  };
  entries.push(entry);
  if (entries.length > MAX) entries.shift();
  /* Still the console, because that is where a developer looks first and this
     must not make debugging harder than it was. */
  console.error(`[${source}]`, value);
  for (const fn of listeners) {
    try { fn(entry); } catch { /* a listener must never break reporting */ }
  }
}

/** Everything recorded so far, oldest first. */
export const getReports = (): readonly ReportEntry[] => entries;

/** How many failures have been recorded, including ones aged out of the ring. */
let total = 0;
export const reportCount = (): number => total;

/** The seam a transport would attach to. Returns an unsubscribe. */
export function subscribe(fn: (e: ReportEntry) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Install the global handlers. Idempotent — the castle mounts and unmounts, and
 * this must not stack a second pair of listeners on a second entry.
 *
 * Deliberately never removed. These outlive any single realm mount by design;
 * a page that has torn the game down can still throw.
 */
export function installGlobalReporting(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('error', (e: ErrorEvent) => {
    report('error', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    report('unhandledrejection', e.reason);
  });
}

/* `total` counts every call, where `entries` only keeps the last MAX. Wrapped
   here rather than incremented inside `report` so the export order reads. */
subscribe(() => { total++; });
